import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextType } from 'af-root/context/fieldReportFullViewFilterContext';

import type { W_WorkRequest_FindAllForFieldReportFilter_VM_WorkRequest } from 'ab-api/web/workRequest/findAllForFieldReportFilter';

import RegularMainFilter from 'af-components/Table/Filters/RegularMainFilter';

import * as WorkRequestActions from 'af-actions/workRequests';

import JobOption from './Shared/JobOption';

class JobFilterOption {
	id: number;
	/** Will technically be job code, but for display purposes */
	label: string;
	jobCode: string;
	title: string;
	customerCompany: Nullable<string>;
	isInternal: boolean;

	constructor(job: W_WorkRequest_FindAllForFieldReportFilter_VM_WorkRequest) {
		this.id = job.id;
		this.label = job.jobCode;
		this.jobCode = job.jobCode;
		this.title = job.title;
		this.customerCompany = job.customerCompany;
		this.isInternal = job.isInternal;
	}

	static bulkConstructor = (_jobs: W_WorkRequest_FindAllForFieldReportFilter_VM_WorkRequest[]) => _jobs.map((_job) => JobFilterOption._constructorMap(_job));

	private static _constructorMap = (_job: W_WorkRequest_FindAllForFieldReportFilter_VM_WorkRequest) => new JobFilterOption(_job);
}

interface OwnProps {
	onChange: (id: number) => void;
	defaultValue?: Nullable<number>;
	forBulkModal?: boolean;
	fieldReportFullViewFilterContext: FieldReportFullViewFilterContextType;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const MainJobFilter: React.FC<Props> = (props) => {

	const {
		onChange,
		findAllForFieldReportFilter,
		forBulkModal,
		defaultValue,
		fieldReportFullViewFilterContext,
	} = props;

	const [options, setOptions] = React.useState<JobFilterOption[] | null>(null);

	const fetchOptions = React.useCallback(async () => {
		const _options = await findAllForFieldReportFilter();
		const formattedOptions = JobFilterOption.bulkConstructor(_options.workRequests);
		setOptions(formattedOptions);
		fieldReportFullViewFilterContext.setJobOptions(formattedOptions);
	}, [fieldReportFullViewFilterContext, findAllForFieldReportFilter]);

	React.useEffect(() => {
		if (!fieldReportFullViewFilterContext.jobOptions.length) {
			fetchOptions();
		} else {
			setOptions(fieldReportFullViewFilterContext.jobOptions);
		}
	}, [fetchOptions, fieldReportFullViewFilterContext.jobOptions, findAllForFieldReportFilter]);

	const renderOption = React.useCallback((option: JobFilterOption) => {
		return (
			<JobOption
				customerCompany={option.customerCompany}
				isInternal={option.isInternal}
				jobCode={option.jobCode}
				title={option.title}
			/>
		);
	}, []);

	const onValueChange = React.useCallback((option: JobFilterOption) => {
		onChange(option.id);
	}, [onChange]);

	const defaultOption = React.useMemo(() => {
		if (options === null) {
			return null;
		}

		return options.find((option) => option.id === defaultValue) ?? undefined;
	}, [options, defaultValue]);

	return (
		<RegularMainFilter
			autoOpen={!forBulkModal && !defaultValue}
			filterName="Job"
			forBulkModal={!!forBulkModal}
			onChange={onValueChange}
			options={options}
			renderOptionOverride={renderOption}
			selected={defaultOption ?? null}
		/>
	);
};

function mapDispatchToProps() {
	return {
		findAllForFieldReportFilter: WorkRequestActions.findAllForFieldReportFilter,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(MainJobFilter);
